import React, { useMemo, useState } from 'react'
import { createFileRoute, Navigate } from '@tanstack/react-router'
import { Trans, useTranslation } from 'react-i18next'
import { Button } from '@workspace/ui/components/Button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@workspace/ui/components/Table'
import { Checkbox } from '@workspace/ui/components/Checkbox'
import { MenuTrigger, Menu, MenuPopover, MenuItem } from '@workspace/ui/components/Menu'
import { UserAvatar } from '@workspace/ui/components/UserAvatar'
import { Plus, Trash2 } from 'lucide-react'
import { Spinner } from '@workspace/ui/components/Spinner'
import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '@workspace/ui/components/Sonner'
import { AddAccountDialog } from '@/shared/components/dialogs/AddAccountDialog'
import { FormDialog } from '@/shared/components/dialogs/FormDialog'
import { requirePermissionBeforeLoad } from '@/shared/lib/authorization/routeAuth'
import { useGetProjectOverview } from '@/shared/hooks/projects/useGetProjectOverview'
import { api } from '@/shared/lib/api'
import { ProjectPermissionAction, User, Department } from '@workspace/lib/api'
import { ADMIN_ROLE_ID } from '@/shared/api/permissions'
import { useInView } from 'react-intersection-observer'
import { AxiosError } from 'axios'
import { useCan } from '@/shared/lib/authorization'
import { useProjectPermissions } from '@/shared/hooks/projects/useProjectPermissions'
import { useUser } from '@/shared/store/auth.store'
import { ADMIN_ROLE_NAME } from '@/shared/constants'

export const Route = createFileRoute('/projects/$id/_pathlessLayout/permissions')({
    component: RouteComponent,
    beforeLoad: requirePermissionBeforeLoad('view', 'project'),
})

type PermissionItem = {
    id: string
    type: 'user' | 'department'
    name: string
    role: string | null
    roleId: string | null
    userId: string | null
    departmentId: string | null
    personnelCount: number | null
    avatar: string | null
    canOperateProject?: boolean
    isNewLocal?: boolean
    isModified?: boolean
    permissions: {
        manage: boolean
        view: boolean
        input: boolean
        download: boolean
    }
}

function RouteComponent() {
    const { t } = useTranslation()
    const { id } = Route.useParams() as { id: string }
    const queryClient = useQueryClient()
    const [permissions, setPermissions] = useState<PermissionItem[]>([])
    const buttonRef = React.useRef<HTMLButtonElement>(null)
    const [menuWidth, setMenuWidth] = React.useState<number | undefined>(undefined)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [dialogType, setDialogType] = useState<'user' | 'department'>('user')
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [itemToDelete, setItemToDelete] = useState<PermissionItem | null>(null)

    const currentUser = useUser()
    const isAdmin = currentUser?.role?.name === ADMIN_ROLE_NAME
    const hasGlobalView = useCan('view', 'project')
    const hasGlobalOperate = useCan('operate', 'project')
    const projectPerm = useProjectPermissions(id)

    const canManagePermissions = isAdmin || (hasGlobalOperate && projectPerm.canManage)
    const canModifyPermissions = isAdmin || (hasGlobalOperate && projectPerm.canInput)
    const canViewPermissions = isAdmin || (hasGlobalView && projectPerm.canView)
    const canDownloadPermissions = isAdmin || (hasGlobalOperate && projectPerm.canDownload)

    const { data: overviewData, isLoading: isLoadingProject } = useGetProjectOverview(id)
    const projectData = overviewData?.project
    const { ref: loadMoreRef, inView } = useInView()

    const {
        data: apiPermissionsData,
        isLoading: isLoadingPermissions,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery({
        queryKey: ['project-permissions-list', id],
        queryFn: ({ pageParam = 1 }) => api.projectPermission.list({ projectId: id, page: pageParam, take: 10 }),
        getNextPageParam: lastPage => (lastPage.meta.hasNextPage ? lastPage.meta.page + 1 : undefined),
        initialPageParam: 1,
        enabled: canViewPermissions,
        meta: { skipGlobalErrorHandler: true },
    })

    React.useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage()
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

    const apiPermissions = useMemo(() => {
        return apiPermissionsData?.pages.flatMap(page => page.data) || []
    }, [apiPermissionsData])

    const isLoading = isLoadingProject || isLoadingPermissions

    const processedPermissions = useMemo(() => {
        if (!apiPermissions) return []

        const groupedPermissions = new Map<string, PermissionItem>()

        apiPermissions.forEach(perm => {
            const key = perm.userId || perm.departmentId || ''
            if (!key) return

            if (!groupedPermissions.has(key)) {
                const roleName = perm.user?.role?.displayName ?? perm.user?.position ?? null
                const roleId = perm.user?.role?.id ?? null
                const canOperateProject = perm.user?.role?.permissions?.some(
                    p => p.resource === 'project' && p.action === 'operate',
                )

                groupedPermissions.set(key, {
                    id: key,
                    type: perm.userId ? 'user' : 'department',
                    name: perm.user?.fullName || perm.department?.name || '',
                    role: roleName,
                    roleId: roleId,
                    userId: perm.userId,
                    departmentId: perm.departmentId,
                    personnelCount:
                        (perm.department as any)?.userCount ?? (perm.department as any)?.personnelCount ?? null,
                    avatar: perm.user?.avatar ?? null,
                    canOperateProject: !!canOperateProject,
                    permissions: {
                        manage: false,
                        view: false,
                        input: false,
                        download: false,
                    },
                })
            }

            const item = groupedPermissions.get(key)!
            switch (perm.action) {
                case ProjectPermissionAction.MANAGER:
                    item.permissions.manage = true
                    item.permissions.view = true
                    item.permissions.input = true
                    item.permissions.download = true
                    break
                case ProjectPermissionAction.READ:
                    item.permissions.view = true
                    break
                case ProjectPermissionAction.MODIFY:
                    item.permissions.input = true
                    break
                case ProjectPermissionAction.DOWNLOAD:
                    item.permissions.download = true
                    break
            }
        })

        return Array.from(groupedPermissions.values())
    }, [apiPermissions])

    // Sync permissions state with processedPermissions
    const prevProcessedRef = React.useRef<string>('')
    React.useEffect(() => {
        const stringified = JSON.stringify(processedPermissions)
        if (prevProcessedRef.current !== stringified) {
            prevProcessedRef.current = stringified

            setPermissions(current => {
                const currentMap = new Map(current.map(p => [p.id, p]))

                // Merge server changes with local edits
                const merged = processedPermissions.map(serverPerm => {
                    const localPerm = currentMap.get(serverPerm.id)
                    if (localPerm && (localPerm.isModified || localPerm.isNewLocal)) {
                        return {
                            ...serverPerm,
                            permissions: localPerm.permissions,
                            isModified: localPerm.isModified,
                            isNewLocal: localPerm.isNewLocal,
                        }
                    }
                    return serverPerm
                })

                // Keep purely local additions
                const serverIds = new Set(processedPermissions.map(p => p.id))
                const localOnly = current.filter(p => !serverIds.has(p.id) && p.isNewLocal)

                return [...merged, ...localOnly]
            })
        }
    }, [processedPermissions])

    React.useEffect(() => {
        const updateMenuWidth = () => {
            if (buttonRef.current) {
                setMenuWidth(buttonRef.current.offsetWidth)
            }
        }

        updateMenuWidth()

        const resizeObserver = new ResizeObserver(updateMenuWidth)
        if (buttonRef.current) {
            resizeObserver.observe(buttonRef.current)
        }

        return () => {
            resizeObserver.disconnect()
        }
    }, [])

    const handlePermissionChange = (
        itemId: string,
        permissionType: 'manage' | 'view' | 'input' | 'download',
        checked: boolean,
    ) => {
        setPermissions(prev =>
            prev.map(item => {
                if (item.id !== itemId) return item

                const updatedPermissions = {
                    ...item.permissions,
                    [permissionType]: checked,
                }
                if (permissionType === 'manage' && checked) {
                    updatedPermissions.view = true
                    updatedPermissions.input = true
                    updatedPermissions.download = true
                }

                return {
                    ...item,
                    permissions: updatedPermissions,
                    isModified: true,
                }
            }),
        )
    }

    const handleDelete = (item: PermissionItem) => {
        setItemToDelete(item)
        setDeleteDialogOpen(true)
    }

    const handleConfirmDelete = async () => {
        if (itemToDelete) {
            try {
                const permissionsToDelete =
                    apiPermissions.filter(p =>
                        itemToDelete.userId
                            ? p.userId === itemToDelete.userId
                            : p.departmentId === itemToDelete.departmentId,
                    ) || []

                const items = permissionsToDelete.map(p => ({
                    id: p.id,
                    action: p.action,
                    isDeleted: true,
                }))

                if (items.length > 0) {
                    await api.projectPermission.bulkUpsert({
                        projectId: id,
                        items,
                    })

                    await queryClient.invalidateQueries({ queryKey: ['project-permissions', id] })
                    await queryClient.invalidateQueries({ queryKey: ['project-permissions-list', id] })
                }

                setPermissions(prev => prev.filter(p => p.id !== itemToDelete.id))

                toast.success({
                    title: t('common.status.success'),
                    description: t('projects.detail.permissions.deleteSuccess'),
                })
            } catch (error) {
                if (error instanceof AxiosError) {
                    // error.response may contain backend details
                }
                toast.error({
                    title: t('common.status.error'),
                    description: t('projects.detail.permissions.deleteError'),
                })
            }
        }
        setDeleteDialogOpen(false)
        setItemToDelete(null)
    }

    const bulkUpsertMutation = useMutation({
        mutationFn: async (newItems: any[]) => {
            const items: any[] = []

            if (apiPermissions && apiPermissions.length > 0) {
                const touchedEntities = new Set(newItems.map(item => item.userId || item.departmentId))

                const newItemsSet = new Set(newItems.map(item => `${item.userId || item.departmentId}-${item.action}`))

                const deletablePermissions = apiPermissions
                    .filter(p => {
                        const entityId = p.userId || p.departmentId
                        // Chỉ xóa nếu user/phòng ban này có nằm trong danh sách vừa bị chỉnh sửa
                        if (!entityId || !touchedEntities.has(entityId)) return false

                        const key = `${entityId}-${p.action}`
                        return !newItemsSet.has(key)
                    })
                    .map(p => ({
                        id: p.id,
                        userId: p.userId,
                        departmentId: p.departmentId,
                        action: p.action,
                        isDeleted: true,
                    }))

                items.push(...deletablePermissions)
            }

            items.push(...newItems)

            const payload = { projectId: id, items }

            return api.projectPermission.bulkUpsert(payload)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['project-permissions', id] })
            queryClient.invalidateQueries({ queryKey: ['project-permissions-list', id] })
            toast.success({
                title: t('common.status.success'),
                description: t('projects.detail.permissions.saveSuccess'),
            })
        },
        onError: (error: AxiosError) => {
            if (error.response?.data) {
                // backend error details available in error.response.data
            }
            toast.error({
                title: t('common.status.error'),
                description: t('projects.detail.permissions.saveError'),
            })
        },
    })

    const handleSave = async () => {
        const items = permissions
            .filter(item => item.isModified || item.isNewLocal)
            .flatMap(item => {
                const actions: any[] = []
                const baseItem = item.userId ? { userId: item.userId } : { departmentId: item.departmentId }

                if (item.permissions.manage) {
                    const existingPerm = apiPermissions.find(
                        p =>
                            p.action === ProjectPermissionAction.MANAGER &&
                            (item.userId ? p.userId === item.userId : p.departmentId === item.departmentId),
                    )
                    actions.push({
                        ...baseItem,
                        action: ProjectPermissionAction.MANAGER,
                        ...(existingPerm?.id && { id: existingPerm.id }),
                    })
                }
                if (item.permissions.view) {
                    const existingPerm = apiPermissions.find(
                        p =>
                            p.action === ProjectPermissionAction.READ &&
                            (item.userId ? p.userId === item.userId : p.departmentId === item.departmentId),
                    )
                    actions.push({
                        ...baseItem,
                        action: ProjectPermissionAction.READ,
                        ...(existingPerm?.id && { id: existingPerm.id }),
                    })
                }
                if (item.permissions.input) {
                    const existingPerm = apiPermissions.find(
                        p =>
                            p.action === ProjectPermissionAction.MODIFY &&
                            (item.userId ? p.userId === item.userId : p.departmentId === item.departmentId),
                    )
                    actions.push({
                        ...baseItem,
                        action: ProjectPermissionAction.MODIFY,
                        ...(existingPerm?.id && { id: existingPerm.id }),
                    })
                }
                if (item.permissions.download) {
                    const existingPerm = apiPermissions.find(
                        p =>
                            p.action === ProjectPermissionAction.DOWNLOAD &&
                            (item.userId ? p.userId === item.userId : p.departmentId === item.departmentId),
                    )
                    actions.push({
                        ...baseItem,
                        action: ProjectPermissionAction.DOWNLOAD,
                        ...(existingPerm?.id && { id: existingPerm.id }),
                    })
                }

                return actions
            })

        bulkUpsertMutation.mutate(items)
    }

    const handleAddUser = () => {
        setDialogType('user')
        setDialogOpen(true)
    }

    const handleAddDepartment = () => {
        setDialogType('department')
        setDialogOpen(true)
    }

    const handleAddAccounts = (selectedItems: (User | Department)[]) => {
        const defaultPermissions = {
            manage: false,
            view: true,
            input: false,
            download: false,
        }

        const newPermissions: PermissionItem[] = selectedItems.map(item => ({
            id: item.id,
            type: dialogType,
            name: dialogType === 'user' ? (item as User).fullName : (item as Department).name,
            role:
                dialogType === 'user'
                    ? (item as User).role?.displayName || (item as User).position || (item as User).role?.name || null
                    : null,
            roleId: dialogType === 'user' ? (item as User).role?.id || null : null,
            userId: dialogType === 'user' ? item.id : null,
            departmentId: dialogType === 'department' ? item.id : null,
            personnelCount: dialogType === 'department' ? (item as Department).userCount || 0 : null,
            avatar: dialogType === 'user' ? (item as User).avatar || null : null,
            canOperateProject:
                dialogType === 'user'
                    ? !!(item as User).role?.permissions?.some(p => p.resource === 'project' && p.action === 'operate')
                    : false,
            isNewLocal: true,
            permissions: defaultPermissions,
        }))

        setPermissions(prev => [...prev, ...newPermissions])
    }

    const existingIds = useMemo(() => {
        return permissions
            .map(permission => (permission.type === 'user' ? permission.userId : permission.departmentId))
            .filter((id): id is string => id !== null)
    }, [permissions])

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-muted-foreground">{t('common.loading')}</p>
            </div>
        )
    }

    return (
        <>
            <div className="border border-border rounded-xl p-4 flex flex-col gap-4">
                <div className="flex justify-between items-center">
                    <h2 className="text-base font-semibold text-text-heading">
                        {t('projects.detail.permissions.title')}
                    </h2>

                    {canManagePermissions && (
                        <div className="flex gap-2">
                            <MenuTrigger>
                                <Button
                                    ref={buttonRef}
                                    variant="outline"
                                    className="flex items-center gap-2 bg-transparent cursor-pointer"
                                >
                                    <Plus className="size-4" />
                                    {t('projects.detail.permissions.addAccount')}
                                </Button>
                                <MenuPopover
                                    style={menuWidth ? { width: `${menuWidth}px` } : undefined}
                                    className={!menuWidth ? 'w-40' : ''}
                                >
                                    <Menu>
                                        <MenuItem onAction={handleAddUser} className="cursor-pointer">
                                            {t('projects.detail.permissions.user')}
                                        </MenuItem>
                                        <MenuItem onAction={handleAddDepartment} className="cursor-pointer">
                                            {t('projects.detail.permissions.department')}
                                        </MenuItem>
                                    </Menu>
                                </MenuPopover>
                            </MenuTrigger>

                            <Button
                                variant="default"
                                onClick={handleSave}
                                isDisabled={bulkUpsertMutation.isPending}
                                className="text-text-accent"
                            >
                                {bulkUpsertMutation.isPending ? t('common.actions.saving') : t('common.actions.save')}
                            </Button>
                        </div>
                    )}
                </div>

                <div className="overflow-x-auto">
                    <Table hideBorder>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="text-sm font-bold text-text-table-head w-[200px]">
                                    {t('projects.detail.permissions.table.account')}
                                </TableHead>
                                <TableHead className="text-center text-sm font-bold text-text-table-head w-[120px]">
                                    {t('projects.detail.permissions.table.manage')}
                                </TableHead>
                                <TableHead className="text-center text-sm font-bold text-text-table-head w-[120px]">
                                    {t('projects.detail.permissions.table.view')}
                                </TableHead>
                                <TableHead className="text-center text-sm font-bold text-text-table-head w-[120px]">
                                    {t('projects.detail.permissions.table.input')}
                                </TableHead>
                                <TableHead className="text-center text-sm font-bold text-text-table-head w-[120px]">
                                    {t('projects.detail.permissions.table.download')}
                                </TableHead>
                                {/* ✅ CHỈ hiện cột Actions khi có quyền manage */}
                                {canManagePermissions && (
                                    <TableHead className="w-12 text-sm font-bold text-text-table-head"></TableHead>
                                )}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {!permissions || permissions.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={canManagePermissions ? 6 : 5}
                                        className="text-center text-muted-foreground py-8"
                                    >
                                        {t('projects.detail.permissions.emptyState')}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                permissions.map(item => (
                                    <TableRow key={item.id}>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                {item.type === 'user' && (
                                                    <UserAvatar
                                                        src={item.avatar}
                                                        alt={item.name}
                                                        fallback={item.name}
                                                        size="md"
                                                    />
                                                )}
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-sm font-medium text-foreground">
                                                        {item.name}
                                                    </span>
                                                    {item.type === 'user' && item.role && (
                                                        <span className="text-xs text-neutral-500">{item.role}</span>
                                                    )}
                                                    {item.type === 'department' && item.personnelCount !== null && (
                                                        <span className="text-xs text-neutral-500">
                                                            {t('projects.detail.permissions.personnelCount', {
                                                                count: item.personnelCount,
                                                            })}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <div className="flex justify-center">
                                                <Checkbox
                                                    isSelected={item.permissions.manage}
                                                    isDisabled={
                                                        !canManagePermissions ||
                                                        (item.type === 'user' && !item.canOperateProject)
                                                    }
                                                    onChange={checked =>
                                                        handlePermissionChange(item.id, 'manage', checked)
                                                    }
                                                />
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <div className="flex justify-center">
                                                <Checkbox
                                                    isSelected={item.permissions.view}
                                                    isDisabled={!canManagePermissions || item.permissions.manage}
                                                    onChange={checked =>
                                                        handlePermissionChange(item.id, 'view', checked)
                                                    }
                                                />
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <div className="flex justify-center">
                                                <Checkbox
                                                    isSelected={item.permissions.input}
                                                    isDisabled={!canManagePermissions || item.permissions.manage}
                                                    onChange={checked =>
                                                        handlePermissionChange(item.id, 'input', checked)
                                                    }
                                                />
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <div className="flex justify-center">
                                                <Checkbox
                                                    isSelected={item.permissions.download}
                                                    isDisabled={!canManagePermissions || item.permissions.manage}
                                                    onChange={checked =>
                                                        handlePermissionChange(item.id, 'download', checked)
                                                    }
                                                />
                                            </div>
                                        </TableCell>
                                        {/* ✅ CHỈ hiện cột Xóa khi có quyền manage */}
                                        {canManagePermissions && (
                                            <TableCell>
                                                {item.id !== projectData?.manager.id && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleDelete(item)}
                                                        className="text-muted-foreground hover:text-destructive"
                                                    >
                                                        <Trash2 className="size-4" />
                                                    </Button>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                    {isFetchingNextPage && (
                        <div className="flex justify-center py-4">
                            <Spinner className="size-5 text-primary" />
                        </div>
                    )}
                    {/* Trigger for infinite scroll */}
                    <div ref={loadMoreRef} className="h-4 w-full" />
                </div>
            </div>

            {/* ✅ CHỈ render dialogs khi có quyền manage */}
            {canManagePermissions && (
                <>
                    <AddAccountDialog
                        open={dialogOpen}
                        onOpenChange={setDialogOpen}
                        type={dialogType}
                        checkPermissionProjectId={id}
                        existingIds={existingIds}
                        onConfirm={handleAddAccounts}
                        regionId={projectData?.region?.id}
                        areaId={projectData?.area?.id}
                    />

                    <FormDialog
                        open={deleteDialogOpen}
                        onOpenChange={setDeleteDialogOpen}
                        title={t('projects.detail.permissions.deleteDialog.title')}
                        description={
                            itemToDelete ? (
                                <Trans
                                    i18nKey={
                                        itemToDelete.type === 'department'
                                            ? 'projects.detail.permissions.deleteDialog.descriptionDepartment'
                                            : 'projects.detail.permissions.deleteDialog.descriptionUser'
                                    }
                                    values={{ name: itemToDelete.name }}
                                    components={{ bold: <span className="font-medium text-foreground" /> }}
                                />
                            ) : undefined
                        }
                        onPrimaryAction={handleConfirmDelete}
                        primaryActionLabel={t('common.actions.delete')}
                        primaryActionVariant="destructive"
                        cancelActionLabel={t('common.actions.cancel')}
                        contentClassName="w-[423px]"
                    />
                </>
            )}
        </>
    )
}
